import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Building2, DollarSign, FileText, Plus, LogIn, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, aud, auDate, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useGet } from "../lib/hooks";
import { PageHeader, Stat, Pill, statusTone } from "../components/Common";
import { RecordDialog } from "../components/RecordDialog";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { InvoiceView } from "./InvoiceView";
import { AssetLibrary } from "../components/AssetLibrary";
import { GlobalSettings } from "../components/GlobalSettings";
import { CustomDomains } from "../components/CustomDomains";
import { DomainAddonPrice } from "../components/sites/DomainDialog";
import { CommsCentre } from "../components/comms/CommsCentre";
import { StorageUsage, SecurityOverview } from "../components/EnterpriseOversight";
import { InvoiceTemplates, NewInvoiceDialog, BalancesDialog } from "../components/billing/Invoicing";

const PAY_LABEL = { card: "Credit card", bank: "Bank transfer" };

const TYPES = ["Health & Community Services", "Professional Services", "Construction & Trades", "Education & Training", "Retail & Hospitality", "Technology & Digital"];

function SubAccounts({ data, plans, reload }) {
  const { setAccountId, reloadAccounts } = useAuth();
  const nav = useNavigate();
  const [edit, setEdit] = useState(null);
  const [bal, setBal] = useState(null);
  const fields = [
    { key: "name", label: "Business name", required: true },
    { key: "business_type", label: "Business type", type: "select", options: TYPES },
    { key: "abn", label: "ABN" }, { key: "email", label: "Email", type: "email" }, { key: "phone", label: "Phone" },
    { key: "plan_id", label: "Plan", type: "select", options: [{ value: "no_charge", label: "No charge (own / internal entity)" }, ...plans.map((p) => ({ value: p.id, label: `${p.name} — ${aud(p.price_aud)}/mo` }))] },
    { key: "status", label: "Status", type: "select", options: ["active", "no_charge", "trial", "suspended", "cancelled"].map((s) => ({ value: s, label: s === "no_charge" ? "No charge" : s[0].toUpperCase() + s.slice(1) })) },
    { key: "trial_start", label: "Trial start date", type: "date", show: (f) => f.status === "trial" },
    { key: "trial_end", label: "Trial end date (reminder email sent 7 days before)", type: "date", show: (f) => f.status === "trial" },
    { key: "address", label: "Address", full: true },
    { key: "payment_method", label: "Payment method (set by you)", type: "select", options: [{ value: "card", label: "Credit card (default)" }, { value: "bank", label: "Bank transfer (invoice with payment terms)" }] },
    { key: "billing_collection", label: "Billing", type: "select", options: [{ value: "manual", label: "Manual: I click Invoice each time" }, { value: "automatic", label: "Automatic: invoice created on the 1st of each month" }] },
    { key: "payment_terms_days", label: "Payment terms (days to pay)", type: "number", show: (f) => f.payment_method === "bank" },
    { key: "is_lab", label: "This is the Development Lab (leave OFF for every real entity)", type: "checkbox", full: true },
  ];
  const save = async (f) => {
    try {
      if (edit.id) await api.put(`/accounts/${edit.id}`, f);
      else await api.post("/accounts", { status: "active", business_type: "Professional Services", payment_method: "card", billing_collection: "manual", ...f });
      toast.success(edit.id ? "Entity account updated" : "Entity account provisioned");
      reload();
      reloadAccounts();
    } catch (e) { toast.error(errMsg(e)); throw e; }
  };
  const invoice = async (id) => {
    try { const { data: inv } = await api.post(`/invoices/generate/${id}`); toast.success(`Tax invoice ${inv.number} generated`); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const enter = (id) => { setAccountId(id); nav("/app/dashboard"); };
  return (
    <>
      <div className="flex justify-end mb-4"><Button onClick={() => setEdit({})} data-testid="create-subaccount-btn"><Plus className="h-4 w-4 mr-1" />New Entity Account</Button></div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {data.accounts.map((a) => (
          <div key={a.id} className="rounded-md border bg-card p-5 space-y-4 fade-up" data-testid={`subaccount-card-${a.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-display text-lg font-bold">{a.name}</div>
                <div className="text-xs text-muted-foreground">{a.business_type} {a.abn && `· ABN ${a.abn}`}</div>
                {a.client_id && <div className="text-[11px] font-mono text-[#C2A462]" data-testid={`client-id-${a.id}`}>Client ID {a.client_id}</div>}
              </div>
              <div className="flex gap-1.5">{a.is_lab && <Pill tone="blue">Dev Lab</Pill>}<Pill tone={statusTone(a.status)}>{a.status === "no_charge" ? "No charge" : a.status}</Pill></div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded border p-2"><div className="overline">Plan</div><div className="text-sm mt-1">{a.plan_id === "no_charge" ? "No charge" : a.plan?.name || "—"}</div></div>
              <div className="rounded border p-2"><div className="overline">Contacts</div><div className="text-sm mt-1">{a.contacts}</div></div>
              <div className="rounded border p-2"><div className="overline">Users</div><div className="text-sm mt-1">{a.users}</div></div>
            </div>
            <div className="rounded border border-[#C2A462]/20 p-2 text-xs space-y-0.5" data-testid={`billing-info-${a.id}`}>
              <div><span className="text-[#C2A462]">{PAY_LABEL[a.payment_method || "card"]}</span> · {a.billing_collection === "automatic" ? "Automatic monthly invoice" : "Manual invoicing"}{a.payment_method === "bank" && ` · ${a.payment_terms_days || 14} day terms`}</div>
              <div className="text-muted-foreground truncate">Billing contact: {[a.billing_name, a.billing_email, a.billing_phone].filter(Boolean).join(" · ") || "not added by the entity yet"}</div>
              {(a.payment_method || "card") === "card" && <div className="text-muted-foreground" data-testid={`card-info-${a.id}`}>Card: {a.card ? `${a.card.brand} •••• ${a.card.last4} · exp ${a.card.exp_month}/${String(a.card.exp_year).slice(-2)}` : "no card added yet"}</div>}
            </div>
            <div className="text-xs font-mono text-muted-foreground">{a.plan_id === "no_charge" || a.status === "no_charge" ? "No charge · not invoiced" : a.plan ? `${aud(a.plan.price_aud)} + GST ${aud(a.plan.price_aud * 0.1)}` : "No plan"} · {a.data_region}</div>
            {a.status === "trial" && <div className="rounded border border-amber-500/30 bg-amber-500/5 p-2 text-xs space-y-0.5" data-testid={`trial-info-${a.id}`}>
              <div>Trial: {a.trial_start ? auDate(a.trial_start) : "start not set"} → {a.trial_end ? auDate(a.trial_end) : "end not set"}</div>
              {(a.trial_log || []).slice(-2).map((l) => <div key={l.at} className="text-muted-foreground">Reminder email {l.status} · {l.to || "—"} · {auDate(l.at)}</div>)}
              {!(a.trial_log || []).length && <div className="text-muted-foreground">Reminder email not sent yet (sent automatically 7 days before the end date)</div>}
            </div>}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => enter(a.id)} data-testid={`enter-subaccount-${a.id}`}><LogIn className="h-3.5 w-3.5 mr-1" />Switch in</Button>
              <Button size="sm" variant="secondary" onClick={() => setEdit(a)} data-testid={`edit-subaccount-${a.id}`}>Edit</Button>
              {a.plan_id !== "no_charge" && a.status !== "no_charge" && <Button size="sm" variant="outline" onClick={() => invoice(a.id)} data-testid={`invoice-subaccount-${a.id}`}>Invoice</Button>}
              <Button size="sm" variant="outline" onClick={() => setBal(a)} data-testid={`balances-subaccount-${a.id}`}>Balances</Button>
            </div>
          </div>
        ))}
      </div>
      {edit && <RecordDialog open onOpenChange={(o) => !o && setEdit(null)} title={edit.id ? "Edit entity account" : "Provision entity account"} fields={fields} initial={edit} onSave={save} />}
      {bal && <BalancesDialog acc={bal} onClose={() => setBal(null)} />}
    </>
  );
}

function Plans({ plans, reload }) {
  const [edit, setEdit] = useState(null);
  const fields = [{ key: "name", label: "Plan name", required: true }, { key: "price_aud", label: "Price (AUD ex GST) / month", type: "number", required: true },
    { key: "storage_gb", label: "Storage limit (GB)", type: "number" },
    { key: "features", label: "Features (one per line)", type: "tagsarea", full: true }, { key: "active", label: "Active", type: "checkbox" }];
  const del = async (p) => {
    if (!window.confirm(`Delete the ${p.name} plan? This can't be undone.`)) return;
    try { await api.delete(`/plans/${p.id}`); toast.success("Plan deleted"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const save = async (f) => {
    try { edit.id ? await api.put(`/plans/${edit.id}`, f) : await api.post("/plans", { active: true, ...f }); toast.success("Plan saved"); reload(); }
    catch (e) { toast.error(errMsg(e)); throw e; }
  };
  return (
    <>
      <div className="flex justify-end mb-4"><Button onClick={() => setEdit({})} data-testid="create-plan-btn"><Plus className="h-4 w-4 mr-1" />New Plan</Button></div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {plans.map((p) => (
          <div key={p.id} className="rounded-md border border-[#C2A462]/25 bg-card p-5" data-testid={`plan-card-${p.id}`}>
            <div className="overline">{p.active ? "Active plan" : "Inactive"}</div>
            <div className="font-display text-xl font-bold mt-1">{p.name}</div>
            <div className="mt-2 text-2xl font-display font-extrabold text-primary">{aud(p.price_aud)}<span className="text-xs text-muted-foreground font-sans">/mo ex GST</span></div>
            <div className="text-xs text-muted-foreground font-mono">{aud(p.price_aud * 1.1)} inc GST</div>
            <ul className="mt-3 space-y-1 text-sm text-slate-300">{(p.features || []).map((f) => <li key={f}>— {f}</li>)}</ul>
            <div className="mt-2 text-xs text-muted-foreground">Storage: {p.storage_gb ?? 5} GB</div>
            <div className="mt-4 flex gap-2"><Button size="sm" variant="secondary" onClick={() => setEdit(p)} data-testid={`edit-plan-${p.id}`}>Edit</Button>
              <Button size="sm" variant="ghost" onClick={() => del(p)} data-testid={`delete-plan-${p.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button></div>
          </div>
        ))}
      </div>
      <DomainAddonPrice />
      {edit && <RecordDialog open onOpenChange={(o) => !o && setEdit(null)} title={edit.id ? "Edit plan" : "New plan"} fields={fields} initial={edit} onSave={save} />}
    </>
  );
}

function Invoices({ invoices, reload, accounts }) {
  const [view, setView] = useState(null);
  const [creating, setCreating] = useState(false);
  const act = async (path, msg) => { try { await api.post(path); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-4">
    <div className="flex justify-end"><Button onClick={() => setCreating(true)} data-testid="new-invoice-btn"><Plus className="h-4 w-4 mr-1" />New invoice</Button></div>
    <InvoiceTemplates />
    {creating && <NewInvoiceDialog accounts={accounts} onClose={() => setCreating(false)} onDone={reload} />}
    <div className="rounded-md border border-[#C2A462]/25 bg-[#16213a] p-4 text-sm text-slate-300 space-y-1" data-testid="billing-howto">
      <div className="font-semibold text-[#EBD6A0]">How invoicing works</div>
      <div>1. Go to Entity Accounts and click <b>Invoice</b> on an entity's card (or set the entity to <b>Automatic</b> so an invoice is created on the 1st of each month). A GST tax invoice is created for its plan, using your Enterprise Details, invoice logo and the entity's billing details.</div>
      <div>2. It appears here. Click the document icon to view or print it, then send it to the entity.</div>
      <div>3. Card entities with a saved card are charged automatically when the invoice is created (use <b>Charge card</b> to retry). For bank transfers, click <b>Mark paid</b> when payment arrives.</div>
    </div>
    <div className="rounded-md border bg-card/60 overflow-x-auto">
      <Table>
        <TableHeader><TableRow>{["Number", "Entity Account", "Issued", "Due", "Payment", "Total", "Status", ""].map((h) => <TableHead key={h} className="overline">{h}</TableHead>)}</TableRow></TableHeader>
        <TableBody>
          {!invoices.length && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No invoices yet. Generate one from an entity account card.</TableCell></TableRow>}
          {invoices.map((i) => (
            <TableRow key={i.id} data-testid={`invoice-row-${i.id}`}>
              <TableCell className="font-mono">{i.number}</TableCell><TableCell>{i.account_name}</TableCell><TableCell>{auDate(i.issue_date)}</TableCell>
              <TableCell>{auDate(i.due_date)}</TableCell><TableCell>{PAY_LABEL[i.payment_method] || "—"}{i.auto ? " · auto" : ""}{i.paid_by && <div className="text-[11px] text-muted-foreground">{i.paid_by}</div>}{i.payment_error && <div className="text-[11px] text-red-400" data-testid={`payment-error-${i.id}`}>{i.payment_error}</div>}</TableCell><TableCell className="font-semibold">{aud(i.total)}</TableCell>
              <TableCell><Pill tone={statusTone(i.status)}>{i.status}</Pill></TableCell>
              <TableCell className="text-right whitespace-nowrap space-x-1">
                <Button size="sm" variant="ghost" onClick={() => setView(i)} data-testid={`view-invoice-${i.id}`}><FileText className="h-3.5 w-3.5" /></Button>
                {i.status !== "paid" && i.payment_method === "card" && <Button size="sm" onClick={() => act(`/invoices/${i.id}/charge`, "Card charge attempted")} data-testid={`charge-card-${i.id}`}>Charge card</Button>}
                {i.status !== "paid" && <Button size="sm" variant="secondary" onClick={() => act(`/invoices/${i.id}/status/paid`, "Marked paid")} data-testid={`mark-paid-${i.id}`}>Mark paid</Button>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {view && <InvoiceView inv={view} onClose={() => setView(null)} />}
    </div>
    </div>
  );
}

function AgencySettings() {
  const [s, reload] = useGet("/agency/settings");
  const [edit, setEdit] = useState(false);
  if (!s) return null;
  const fields = [{ key: "name", label: "Enterprise / trading name" }, { key: "abn", label: "ABN" }, { key: "email", label: "Billing email" }, { key: "phone", label: "Billing phone" },
    { key: "address", label: "Address", full: true },
    { key: "bank_account_name", label: "Bank account name" }, { key: "bank_bsb", label: "BSB" }, { key: "bank_account_number", label: "Account number" },
    { key: "default_payment_terms", label: "Default payment terms for bank transfer (days)", type: "number" }];
  return (
    <div className="space-y-4">
    <div className="rounded-md border bg-card p-6 w-full space-y-2" data-testid="agency-settings">
      {fields.map((f) => <div key={f.key} className="flex justify-between text-sm border-b py-2"><span className="text-muted-foreground">{f.label}</span><span>{s[f.key] || "—"}</span></div>)}
      <Button size="sm" className="mt-3" onClick={() => setEdit(true)} data-testid="edit-agency-settings">Edit details</Button>
      {edit && <RecordDialog open onOpenChange={setEdit} title="Enterprise details (appear on tax invoices)" fields={fields} initial={s}
        onSave={async (f) => { await api.put("/agency/settings", f); toast.success("Saved"); reload(); }} />}
    </div>
    <SharedSupport s={s} reload={reload} />
    </div>
  );
}

const SUPPORT = [{ key: "support_phone", label: "Support phone number" }, { key: "support_email", label: "Support email", type: "email" }];

function SharedSupport({ s, reload }) {
  const [edit, setEdit] = useState(false);
  return (
    <div className="rounded-md border bg-card p-6 w-full space-y-2" data-testid="shared-support-card">
      <div className="font-semibold text-[#EBD6A0]">Shared support details</div>
      <p className="text-xs text-muted-foreground">Shown under Our Support in every entity's sidebar. An entity that enters its own support details in Settings › Systems uses those instead.</p>
      {SUPPORT.map((f) => <div key={f.key} className="flex justify-between text-sm border-b py-2"><span className="text-muted-foreground">{f.label}</span><span data-testid={`shared-${f.key}`}>{s[f.key] || "—"}</span></div>)}
      <Button size="sm" className="mt-3" onClick={() => setEdit(true)} data-testid="edit-shared-support">Edit support details</Button>
      {edit && <RecordDialog open onOpenChange={setEdit} title="Shared support details" fields={SUPPORT} initial={s}
        onSave={async (f) => { await api.put("/agency/settings", { support_phone: f.support_phone || "", support_email: f.support_email || "" }); toast.success("Support details saved"); reload(); window.dispatchEvent(new Event("bos-support-updated")); }} />}
    </div>
  );
}

const SECTIONS = {
  entities: ["Entity Accounts", "Provision, bill and manage every entity account you sell. All pricing in AUD with 10% GST itemised."],
  plans: ["Plans", "Monthly subscription plans offered to entity accounts."],
  billing: ["Billing & Invoices", "GST-compliant tax invoices issued to entity accounts."],
  details: ["Enterprise Details", "Your business details shown on every tax invoice."],
  theme: ["Global Settings", "Colours, fonts and sizes used across all of BOS HQ. Changes apply to every entity."],
  domains: ["Custom Domains", "Connect your own web address (e.g. app.boshq.com.au) to BOS HQ."],
  comms: ["Communication Centre", "Email sending, SMS settings, email templates and announcements."],
  storage: ["Storage Usage", "How much file storage each entity is using."],
  security: ["Security", "Sign-in protection, recent sign-ins and backups across BOS HQ."],
  assets: ["Media Library", "Enterprise-wide images: login background, login logo, sidebar logo, favicon and email logo."],
};

export default function Agency() {
  const { tab = "entities" } = useParams();
  const [data, reload] = useGet("/agency/overview");
  const [plans, reloadPlans] = useGet("/plans");
  const [invoices, reloadInv] = useGet("/invoices");
  const all = () => { reload(); reloadPlans(); reloadInv(); };
  const [title, desc] = SECTIONS[tab] || SECTIONS.entities;
  return (
    <div data-testid={`agency-section-${tab}`}>
        <PageHeader overline="Enterprise HQ" title={title} desc={desc} />
        {tab === "entities" && data && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Stat label="Active entity accounts" value={data.active} icon={Building2} testid="stat-active-accounts" />
            <Stat label="Monthly revenue · MRR (ex GST)" value={aud(data.mrr_ex_gst)} icon={DollarSign} testid="stat-mrr" />
            <Stat label="Monthly revenue · MRR (inc GST)" value={aud(data.mrr_inc_gst)} icon={DollarSign} accent testid="stat-mrr-gst" />
            <Stat label="Unpaid invoices" value={data.unpaid_invoices} icon={FileText} accent testid="stat-unpaid" />
          </div>
        )}
        {tab === "entities" && data && <p className="-mt-5 mb-6 text-xs text-muted-foreground" data-testid="mrr-explainer">MRR = Monthly Recurring Revenue: the total of all active entity plans billed each month (trials and no-charge entities are not included).</p>}
        {tab === "entities" && data && plans && <SubAccounts data={data} plans={plans} reload={all} />}
        {tab === "plans" && plans && <Plans plans={plans} reload={all} />}
        {tab === "billing" && invoices && <Invoices invoices={invoices} reload={all} accounts={data?.accounts || []} />}
        {tab === "details" && <AgencySettings />}
        {tab === "assets" && <AssetLibrary scope="agency" />}
        {tab === "theme" && <GlobalSettings />}
        {tab === "domains" && <CustomDomains accounts={data?.accounts || []} />}
        {tab === "comms" && <CommsCentre accounts={data?.accounts || []} />}
        {tab === "storage" && <StorageUsage />}
        {tab === "security" && <SecurityOverview />}
    </div>
  );
}
